import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { registerPushSubscription } from '../utils/pushSubscribe';
import { authHeaders } from '../utils/authHeaders';

function decodeToken() {
  try {
    const token = localStorage.getItem('paper_work_token');
    if (!token) return null;
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    const payload = JSON.parse(json);
    if (!payload.name) payload.name = payload.username;
    return payload;
  } catch { return null; }
}

export const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  const socketRef = useRef(null);
  const [socket, setSocket] = useState(null);
  const activeChannelRef = useRef(null);
  const isTypingRef = useRef(false);
  const typingTimerRef = useRef(null);
  const timerRefs = useRef({});
  const chatOpenRef = useRef(false);
  const pushRequestedRef = useRef(false);
  const isInitialLoadRef = useRef(false);

  const me = decodeToken();

  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [loading, setLoading] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  const [requestChannelId, setRequestChannelId] = useState(null);
  const [meetingChannel, setMeetingChannel] = useState(null);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [readMap, setReadMap] = useState({});         // {userId: lastReadAt} for active channel
  const [channelMemberCount, setChannelMemberCount] = useState(0);

  // ── Google Chat 전역 상태 ────────────────────────────────────────────────────
  const [gchatUnreadBySpace, setGchatUnreadBySpace] = useState({});
  const gchatMyGoogleIdRef = useRef(null);
  const gchatActiveSpaceRef = useRef(null); // 현재 보고 있는 spaceId

  const gchatUnread = Object.values(gchatUnreadBySpace).reduce((a, b) => a + b, 0);

  useEffect(() => {
    localStorage.setItem('gchat_unread', String(gchatUnread));
    window.dispatchEvent(new CustomEvent('gchat-unread', { detail: { count: gchatUnread } }));
  }, [gchatUnread]);

  const clearGchatSpace = useCallback(spaceId => {
    setGchatUnreadBySpace(prev => { const n = { ...prev }; delete n[spaceId]; return n; });
  }, []);

  const setGchatActiveSpace = useCallback(spaceId => {
    gchatActiveSpaceRef.current = spaceId;
  }, []);

  const loadChannels = useCallback(() => {
    fetch('/api/chat/channels', { headers: authHeaders() })
      .then(r => r.json())
      .then(data => setChannels(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('paper_work_token');
    if (!token) return;
    const socket = io({ auth: { token }, transports: ['websocket'] });
    socketRef.current = socket;
    setSocket(socket);
    socket.on('connect', () => console.log('[socket] connected', socket.id));
    socket.on('connect_error', e => console.error('[socket] connect_error', e.message));

    socket.on('new_message', msg => {
      if (activeChannelRef.current?.id === msg.channelId) {
        setMessages(prev => [...prev, msg]);
        if (chatOpenRef.current) socket.emit('mark_read', { channelId: msg.channelId });
      }
      // 해당 채널을 목록 맨 위로
      setChannels(prev => {
        const idx = prev.findIndex(ch => Number(ch.id) === Number(msg.channelId));
        if (idx <= 0) return prev;
        const updated = [...prev];
        const [ch] = updated.splice(idx, 1);
        return [{ ...ch, lastMessage: { content: msg.content, createdAt: msg.createdAt, userId: msg.userId, user: msg.user } }, ...updated];
      });
    });

    socket.on('new_message_notify', msg => {
      const myId = decodeToken()?.id;
      if (msg.userId === myId) return;
      if (activeChannelRef.current?.id !== msg.channelId) {
        setChannels(prev => {
          const idx = prev.findIndex(ch => Number(ch.id) === Number(msg.channelId));
          if (idx < 0) return prev;
          const updated = [...prev];
          const [ch] = updated.splice(idx, 1);
          return [{ ...ch, unreadCount: (ch.unreadCount || 0) + 1, lastMessage: { content: msg.content, createdAt: msg.createdAt, userId: msg.userId, user: msg.user } }, ...updated];
        });
      }
      const isViewing = chatOpenRef.current && activeChannelRef.current?.id === msg.channelId;
      if (!isViewing) {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev.slice(-2), { id, msg }]);
        timerRefs.current[id] = setTimeout(() => {
          setToasts(prev => prev.filter(t => t.id !== id));
          delete timerRefs.current[id];
        }, 5000);
      }
    });

    socket.on('user_typing', ({ userId, userName }) =>
      setTypingUsers(prev => ({ ...prev, [userId]: userName })));
    socket.on('user_stop_typing', ({ userId }) =>
      setTypingUsers(prev => { const n = { ...prev }; delete n[userId]; return n; }));
    socket.on('message_deleted', ({ messageId }) =>
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, deleted: true } : m)));

    socket.on('reaction_update', ({ messageId, reactions }) =>
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions } : m)));

    socket.on('message_edited', ({ messageId, content }) =>
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, content, edited: true } : m)));

    socket.on('joined', ({ readMap: rm, memberCount }) => {
      if (rm) setReadMap(rm);
      if (memberCount != null) setChannelMemberCount(memberCount);
    });

    socket.on('read_receipt', ({ channelId, userId, readAt }) => {
      if (activeChannelRef.current?.id === channelId) {
        setReadMap(prev => ({ ...prev, [userId]: readAt }));
      }
    });

    socket.on('mentioned', msg => {
      const myId = decodeToken()?.id;
      const isViewing = chatOpenRef.current && activeChannelRef.current?.id === msg.channelId;
      if (!isViewing) {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev.slice(-2), { id, msg, isMention: true }]);
        timerRefs.current[id] = setTimeout(() => {
          setToasts(prev => prev.filter(t => t.id !== id));
          delete timerRefs.current[id];
        }, 7000);
      }
    });

    // ── Google Chat 전역 구독 ────────────────────────────────────────────────
    // 알림 권한 요청
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    // 스페이스 목록 로드 → 소켓 룸 구독 (백그라운드)
    if (token) {
      fetch('/api/google/chat/spaces', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (!data) return;
          if (data.myId) gchatMyGoogleIdRef.current = data.myId;
          const spaces = Array.isArray(data.spaces) ? data.spaces : [];
          spaces.slice(0, 20).forEach(sp => {
            socket.emit('watch_google_space', { spaceId: sp.spaceId, spaceDisplayName: sp.displayName });
          });
          console.log(`[gchat] 전역 구독 ${spaces.length}개 스페이스`);
        })
        .catch(() => {});
    }

    // google_chat_messages 전역 핸들러 (unread + OS알림)
    const gchatHandler = ({ spaceId, messages: incoming }) => {
      const myGoogleId = gchatMyGoogleIdRef.current;
      const fromOthers = incoming.filter(m =>
        myGoogleId ? m.senderName !== `users/${myGoogleId}` : !m.isMe
      );
      if (!fromOthers.length) return;
      if (gchatActiveSpaceRef.current === spaceId) return; // 현재 보는 스페이스 → 무시

      setGchatUnreadBySpace(prev => ({ ...prev, [spaceId]: (prev[spaceId] || 0) + fromOthers.length }));

      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        fromOthers.slice(0, 3).forEach(msg => {
          new Notification(msg.sender || 'Google Chat', {
            body: msg.text?.slice(0, 100) || '📎 파일',
            icon: '/favicon.svg',
            tag: `gchat-${msg.name}`,
          });
        });
      }
    };
    socket.on('google_chat_messages', gchatHandler);

    return () => {
      socket.off('google_chat_messages', gchatHandler);
      socket.disconnect();
    };
  }, []);

  useEffect(() => { loadChannels(); }, [loadChannels]);

  useEffect(() => {
    const total = channels.reduce((sum, ch) => sum + (ch.unreadCount || 0), 0);
    setTotalUnread(total);
  }, [channels]);

  const selectChannel = useCallback(async ch => {
    activeChannelRef.current = ch;
    setActiveChannel(ch);
    setMessages([]);
    setLoading(true);
    setTypingUsers({});
    setShowMembers(false);
    setReadMap({});
    setChannelMemberCount(0);
    try {
      const res = await fetch(`/api/chat/channels/${ch.id}/messages`, { headers: authHeaders() });
      const data = await res.json();
      isInitialLoadRef.current = true;
      setMessages(data.messages || []);
    } catch { setMessages([]); } finally { setLoading(false); }
    socketRef.current?.emit('join_channel', ch.id);
    setChannels(prev => prev.map(c => c.id === ch.id ? { ...c, unreadCount: 0 } : c));
  }, []);

  const stopTyping = useCallback(() => {
    if (isTypingRef.current && activeChannelRef.current) {
      socketRef.current?.emit('typing_stop', { channelId: activeChannelRef.current.id });
      isTypingRef.current = false;
    }
    clearTimeout(typingTimerRef.current);
  }, []);

  const handleInputChange = useCallback(e => {
    setNewMsg(e.target.value);
    if (!activeChannelRef.current) return;
    if (!isTypingRef.current) {
      socketRef.current?.emit('typing_start', { channelId: activeChannelRef.current.id });
      isTypingRef.current = true;
    }
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(stopTyping, 2000);
  }, [stopTyping]);

  const sendMessage = useCallback(() => {
    const content = newMsg.trim();
    if (!content || !activeChannelRef.current) return;
    socketRef.current?.emit('send_message', { channelId: activeChannelRef.current.id, content });
    setNewMsg('');
    stopTyping();
  }, [newMsg, stopTyping]);

  const handleFileSelect = useCallback(async (file) => {
    if (!file || !activeChannelRef.current) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('paper_work_token');
      const res = await fetch('/api/chat/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (data.url) {
        socketRef.current?.emit('send_message', {
          channelId: activeChannelRef.current.id,
          content: JSON.stringify({ url: data.url, name: data.name, size: data.size, mime: data.mime }),
          type: 'file',
        });
      }
    } catch {} finally { setUploading(false); }
  }, []);

  const createChannel = useCallback(async form => {
    const res = await fetch('/api/chat/channels', { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
    const created = await res.json();
    if (created?.id) setChannels(prev => [...prev, { ...created, unreadCount: 0 }]);
  }, []);

  const renameChannel = useCallback(async (id, { name, description }) => {
    const body = {};
    if (name !== undefined) body.name = name;
    if (description !== undefined) body.description = description;
    const res = await fetch(`/api/chat/channels/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body) });
    const updated = await res.json();
    if (updated?.id) {
      setChannels(prev => prev.map(ch => ch.id === id ? { ...ch, name: updated.name, description: updated.description } : ch));
      if (activeChannelRef.current?.id === id) setActiveChannel(a => ({ ...a, name: updated.name, description: updated.description }));
    }
  }, []);

  const reorderChannel = useCallback(async (id, sortOrder) => {
    await fetch(`/api/chat/channels/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ sortOrder }) });
  }, []);

  const deleteChannel = useCallback(async ch => {
    if (!confirm(`'${ch.name}' 채널을 삭제하시겠습니까?`)) return;
    await fetch(`/api/chat/channels/${ch.id}`, { method: 'DELETE', headers: authHeaders() });
    setChannels(prev => prev.filter(c => c.id !== ch.id));
    if (activeChannelRef.current?.id === ch.id) {
      setActiveChannel(null);
      activeChannelRef.current = null;
    }
  }, []);

  const dismissToast = useCallback(id => {
    clearTimeout(timerRefs.current[id]);
    delete timerRefs.current[id];
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const openToastChannel = useCallback((channelId, toastId) => {
    dismissToast(toastId);
    setRequestChannelId(channelId);
  }, [dismissToast]);

  const sendReaction = useCallback((messageId, emoji) => {
    socketRef.current?.emit('add_reaction', { messageId, emoji });
  }, []);

  const editMessage = useCallback((messageId, content) => {
    socketRef.current?.emit('edit_message', { messageId, content });
  }, []);

  const openDm = useCallback(async (targetUser) => {
    const res = await fetch(`/api/chat/dm/${targetUser.id}`, { headers: authHeaders() });
    if (!res.ok) return;
    const channel = await res.json();
    if (!channel?.id) return;
    setChannels(prev => {
      const exists = prev.find(c => c.id === channel.id);
      if (exists) return prev.map(c => c.id === channel.id ? { ...c, unreadCount: 0, dmPartner: targetUser } : c);
      return [{ ...channel, unreadCount: 0, dmPartner: targetUser }, ...prev];
    });
    selectChannel({ ...channel, dmPartner: targetUser });
  }, [selectChannel]);

  const notifyChatOpen = useCallback((isOpen) => {
    chatOpenRef.current = isOpen;
    if (isOpen && !pushRequestedRef.current) {
      const token = localStorage.getItem('paper_work_token');
      if (token) { pushRequestedRef.current = true; registerPushSubscription(); }
    }
  }, []);

  return (
    <ChatContext.Provider value={{
      me, socketRef, socket,
      channels, setChannels, activeChannel, messages, typingUsers, loading,
      totalUnread, toasts, newMsg, uploading,
      showNewModal, setShowNewModal,
      renameTarget, setRenameTarget,
      showMembers, setShowMembers,
      requestChannelId, setRequestChannelId,
      meetingChannel, setMeetingChannel,
      meetingOpen, setMeetingOpen,
      isInitialLoadRef,
      selectChannel, handleInputChange, sendMessage, handleFileSelect,
      createChannel, renameChannel, deleteChannel, openDm,
      loadChannels, dismissToast, openToastChannel, notifyChatOpen,
      reorderChannel, sendReaction, editMessage,
      setNewMsg,
      readMap, channelMemberCount,
      gchatUnreadBySpace, gchatUnread, gchatMyGoogleIdRef, gchatActiveSpaceRef,
      clearGchatSpace, setGchatActiveSpace, setGchatUnreadBySpace,
    }}>
      {children}
    </ChatContext.Provider>
  );
}

